import React from "react";
import Pagination from "./Pagination";
import {
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowsUpDownIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";

const Table = ({
  columns,
  data = [], // Default to an empty array if data is undefined or null
  onRowClick,
  onDeleteRow, // New prop for delete functionality
  actions = [], // Default to an empty array if actions is undefined or null
  title,
  description,
  showPagination,
  currentPage,
  totalPages,
  onPageChange,
  onSortChange,
  totalResults,
  sortConfig,
  highlightField, // New prop to specify which boolean field to check
  pageSize = 10, // Default pageSize
}) => {
  // Helper function to get nested property
  const getNestedProperty = (obj, path) => {
    if (typeof path === "function") {
      // If the accessor is a function, call it with the object (row)
      return path(obj);
    }

    // If the accessor is a string, use it to get the nested property
    return path
      .split(/[\.\[\]]/)
      .filter(Boolean)
      .reduce((acc, part) => acc && acc[part], obj);
  };

  // Helper function to render cell content
  const renderCellContent = (item, column) => {
    const value = getNestedProperty(item, column.accessor);
    if (column.accessor === "color") {
      return (
        <div className="flex items-center">
          <div
            className="w-6 h-6 rounded-full mr-2"
            style={{ backgroundColor: value }}
          />
        </div>
      );
    }
    return value;
  };

  // Sorting function
  const requestSort = (key, sortable) => {
    console.log(
      "[TABLE] Requesting sort for:",
      key,
      "with sortable:",
      sortable
    );
    if (!sortable || !onSortChange) return;

    let direction = "ascending";
    if (sortConfig?.key === key && sortConfig?.direction === "ascending") {
      direction = "descending";
    }

    onSortChange(key); // Only pass the key, let parent handle direction logic
  };

  return (
    <div className="w-full space-y-4">
      {/* Header Section */}
      {(title || description || actions.length > 0) && (
        <div className="flex items-center justify-between">
          <div className="space-y-1.5">
            {title && (
              <h2 className="text-base font-semibold tracking-tight text-gray-900">
                {title}
              </h2>
            )}
            {description && (
              <p className="text-xs text-gray-500">{description}</p>
            )}
          </div>
          {actions.length > 0 && (
            <div className="flex items-center gap-2">
              {actions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.onClick}
                  className="inline-flex items-center justify-center rounded-sm text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/20 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-indigo-600 text-white hover:bg-indigo-500 h-8 px-3 py-1.5 shadow-sm">
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Table Section */}
      <div className="rounded-sm border border-gray-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50/80">
              <tr className="border-b transition-colors hover:bg-gray-50/80">
                {columns.map((column) => (
                  <th
                    key={column.accessor}
                    className={`h-10 px-3 text-left align-middle font-medium text-gray-500 ${
                      column.sortable ? "cursor-pointer" : ""
                    }`}
                    onClick={() =>
                      requestSort(column.accessor, column.sortable)
                    }>
                    <div className="flex items-center gap-1.5">
                      {column.header}
                      {column.sortable && (
                        <span className="flex-none text-gray-400">
                          {sortConfig?.key === column.accessor ? (
                            sortConfig.direction === "ascending" ? (
                              <ChevronUpIcon className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronDownIcon className="h-3.5 w-3.5" />
                            )
                          ) : (
                            <ArrowsUpDownIcon className="h-3.5 w-3.5" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                <th className="h-10 px-3 text-left align-middle font-medium text-gray-500">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.map((item, index) => (
                <tr
                  key={item._id}
                  onClick={onRowClick ? () => onRowClick(item._id) : undefined}
                  className={`border-b transition-colors hover:bg-gray-100 ${onRowClick ? 'cursor-pointer' : ''} ${
                    highlightField && item[highlightField]
                      ? "bg-yellow-50/60"
                      : index % 2 === 0 
                        ? "bg-white" 
                        : "bg-gray-50"
                  }`}>
                  {columns.map((column) => (
                    <td
                      key={column.accessor}
                      className="p-3 align-middle">
                      <div className="flex items-center gap-2">
                        {renderCellContent(item, column)}
                      </div>
                    </td>
                  ))}
                  <td className="p-1 align-middle text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick(item._id);
                        }}
                        className="inline-flex items-center justify-center rounded-sm text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/20 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background hover:bg-gray-100 h-7 w-7 p-0">
                        <EyeIcon className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                        <span className="sr-only">View {item._id}</span>
                      </button>
                      {onDeleteRow && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteRow(item._id);
                          }}
                          className="inline-flex items-center justify-center rounded-sm text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/20 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background hover:bg-gray-100 h-7 w-7 p-0">
                          <TrashIcon className="h-4 w-4 text-red-500 hover:text-red-700" />
                          <span className="sr-only">Delete {item._id}</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {showPagination && (
        <div className="mt-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            totalResults={totalResults}
            pageSize={pageSize}
          />
        </div>
      )}
    </div>
  );
};

export default Table;
